import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { MoreVertIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Box from "components/Box";
import ButtonDropdown from "components/ButtonDropdown";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import { MenuItem } from "components/Menu";
import PageHeader from "components/PageHeader";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import {
  openAIDocumentAssistantDelete,
  useOpenAIDocumentAssistant,
  useOpenAIDocumentTypes,
  usePromptList,
} from "entities/AISummaries/sdk";
import UpdateDocumentTypeDialog from "entities/Field/components/Filefield/AISummariesDrawer/UpdateDocumentTypeDialog";

import FilesDialog from "./FilesDialog";

export interface IAISummaryPageHeader {
  openAIDocumentAssistantId: number | null;
  deleteCallback: () => void;
  backLink?: string;
  backTitle?: string;
}

const AISummaryPageHeader = ({
  openAIDocumentAssistantId,
  deleteCallback,
  backLink,
  backTitle,
}: IAISummaryPageHeader) => {
  const [isUpdateDocumentTypeDialogOpen, setIsUpdateDocumentTypeDialogOpen] =
    useState(false);

  const [filesDialogIsOpen, setFilesDialogIsOpen] = useState(false);

  const { data: documentTypes, isLoading: documentTypesAreLoading } =
    useOpenAIDocumentTypes();

  const {
    data: documentAssistant,
    isLoading: documentAssistantIsLoading,
    mutate: mutateDocumentAssistant,
  } = useOpenAIDocumentAssistant({
    openAIDocumentAssistantId,
  });

  const { data: prompts, mutate: mutatePrompts } = usePromptList({
    openAIDocumentAssistantId,
  });

  const documentTypeName = useMemo(
    () =>
      _.find(documentTypes, ["id", documentAssistant?.openai_document_type])
        ?.name || "Unknown",
    [documentTypes, documentAssistant]
  );

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const handleSummaryDelete = useCallback(() => {
    if (!_.isNull(openAIDocumentAssistantId)) {
      showConfirmationDialog({
        onConfirm: () =>
          openAIDocumentAssistantDelete({
            assistantId: openAIDocumentAssistantId,
          })
            .then(() => {
              deleteCallback();
              toast.successMessage("File has been deleted successfully!");
            })
            .catch(handleInvalidRequest),
        confirmButtonText: "Delete",
        message: "Are you sure you want to delete this file?",
      });
    }
  }, [openAIDocumentAssistantId, deleteCallback, showConfirmationDialog]);

  const hasOpenaAIAnswerInProcess = useMemo(() => {
    if (!_.isNil(prompts)) {
      return prompts.some((prompt) =>
        prompt.prompt_responses.some((response) =>
          _.isNil(response.readable_answer)
        )
      );
    }

    return false;
  }, [prompts]);

  const reasonForButtonBeingDisabled = useMemo(() => {
    if (hasOpenaAIAnswerInProcess) {
      return "Please wait for the current answer to be generated.";
    }

    return "";
  }, [hasOpenaAIAnswerInProcess]);

  const handleUpdateDocumentType = useCallback(() => {
    setIsUpdateDocumentTypeDialogOpen(false);
    mutateDocumentAssistant();
    mutatePrompts();
  }, [mutateDocumentAssistant, mutatePrompts]);

  return (
    <>
      {!_.isUndefined(documentAssistant) && (
        <UpdateDocumentTypeDialog
          isOpen={isUpdateDocumentTypeDialogOpen}
          initialDocumentTypeId={documentAssistant.openai_document_type}
          openAIDocumentAssistantId={documentAssistant.id}
          onUpdate={handleUpdateDocumentType}
          onClose={() => setIsUpdateDocumentTypeDialogOpen(false)}
        />
      )}
      <FilesDialog
        isOpen={filesDialogIsOpen}
        files={documentAssistant?.files || []}
        onClose={() => setFilesDialogIsOpen(false)}
      />
      <Stack spacing={4}>
        <PageHeader
          backLink={backLink}
          backTitle={backTitle}
          title="AI Summary"
          actions={
            <ButtonDropdown
              icon={<MoreVertIcon />}
              label=""
              variant="text"
              size="small"
              width="auto"
              sx={{
                padding: theme.spacing(0.5, 0.75),
              }}
            >
              <GroupLabel label="Settings" />
              <MenuItem onClick={() => setFilesDialogIsOpen(true)}>
                View original documents
              </MenuItem>
              <Tooltip title={reasonForButtonBeingDisabled}>
                <span>
                  <MenuItem
                    disabled={hasOpenaAIAnswerInProcess}
                    onClick={() => setIsUpdateDocumentTypeDialogOpen(true)}
                  >
                    <Text variant="text2">Modify document type</Text>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip title={reasonForButtonBeingDisabled}>
                <span>
                  <MenuItem
                    disabled={hasOpenaAIAnswerInProcess}
                    onClick={handleSummaryDelete}
                  >
                    Delete summary
                  </MenuItem>
                </span>
              </Tooltip>
            </ButtonDropdown>
          }
        />

        <Stack direction="row" spacing={10}>
          <Box>
            <Text variant="label" color={colors.gray60} fontWeight={600}>
              Document type
            </Text>
            <Text variant="text2" fontWeight={700}>
              {documentAssistantIsLoading || documentTypesAreLoading ? (
                <Skeleton height="20px" />
              ) : (
                documentTypeName
              )}
            </Text>
          </Box>
          <Box>
            <Text variant="label" color={colors.gray60} fontWeight={600}>
              Source documents
            </Text>
            <Text variant="text2" color={colors.gray80}>
              {documentAssistantIsLoading ? (
                <Skeleton height="20px" />
              ) : (
                documentAssistant?.files
                  ?.map((file) => file.original_file_name)
                  .join(", ")
              )}
            </Text>
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default AISummaryPageHeader;
